import styled from '@emotion/styled';
import { NumericFormat } from 'react-number-format';
import { bluePlanetTheme } from 'ui/theme';
import React from 'react';

const StyledNumericFormat = styled(NumericFormat)<{ width?: string }>`
  width: ${props => props.width || '100%'};
  max-width: 12.5rem;
  text-align: right;
  padding: 10px;
  border: 1px solid ${bluePlanetTheme.bluePlanetPalette.inputBorder};
  border-radius: ${bluePlanetTheme.shape.borderRadius}px;
  background-color: ${bluePlanetTheme.bluePlanetPalette.white};
  transition: ${bluePlanetTheme.transitions.create(['border-color'], {
    duration: bluePlanetTheme.transitions.duration.standard,
    easing: bluePlanetTheme.transitions.easing.easeOut,
  })};
  outline: none;
  font-family: ${bluePlanetTheme.typography.fontFamily};
  font-size: ${bluePlanetTheme.typography.body1.fontSize};
  font-weight: 400;
  box-sizing: border-box;
  color: ${bluePlanetTheme.bluePlanetPalette.indigo.main};
  opacity: 1;

  &:focus {
    color: ${bluePlanetTheme.bluePlanetPalette.indigo.main};
    border-color: ${bluePlanetTheme.bluePlanetPalette.grey.main};
  }
`;

export interface KpiNumericInputProps {
  value?: string | number;
  width?: string;
  autoFocus?: boolean;
  placeholder?: string;
  disabled?: boolean;
  getInputRef?: (ref: HTMLInputElement | null) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocusCapture?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const KpiNumericInput = React.forwardRef<HTMLInputElement, KpiNumericInputProps>(
  (
    { width, onFocusCapture, getInputRef, value, autoFocus, placeholder, disabled, onChange, onBlur, onKeyDown },
    ref,
  ) => {
    const handleFocusCapture = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
      onFocusCapture?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!onChange) return;

      const validNumberPattern = /^-?[0-9\b]*(\.[0-9]{0,2})?$/;
      const newValue = e.target.value.replace(/\s/g, ''); // remove whitespace

      if (newValue === '' || validNumberPattern.test(newValue)) {
        onChange(e);
      }
    };

    return (
      <StyledNumericFormat
        value={value}
        width={width}
        autoFocus={autoFocus}
        placeholder={placeholder}
        disabled={disabled}
        getInputRef={getInputRef || ref}
        thousandSeparator={' '}
        decimalSeparator={'.'}
        decimalScale={2}
        allowNegative={true}
        onFocusCapture={handleFocusCapture}
        onChange={handleChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
    );
  },
);

KpiNumericInput.displayName = 'KpiNumericInput';
